/**
 * Magic Workshop tutorials, shown the first time a level with that id
 * opens. The first levels teach one control each; every chapter's first
 * level introduces its new idea. `focus` tells the scene (shelf, box, bows,
 * scissors, threads, demo) or the HUD (target, undo, hint) what to
 * highlight; "demo" keeps a sparkle on the next good roll until it is laid.
 */
export type SilkTutorialFocus = "shelf" | "box" | "bows" | "scissors" | "threads" | "demo" | "target" | "undo" | "hint";

export interface SilkTutorialStep {
  readonly text: string;
  readonly focus?: SilkTutorialFocus;
}

export interface SilkTutorial {
  readonly id: string;
  readonly steps: readonly SilkTutorialStep[];
}

export const SILK_TUTORIALS: readonly SilkTutorial[] = [
  {
    id: "silk-basics",
    steps: [
      { text: "Chào mừng tới Xưởng Phép Thuật! Khách đặt một hộp quà, mình gói giúp bằng lụa phép nha.", focus: "box" },
      { text: "Trên kệ là các cuộn lụa phép. Mỗi cuộn chỉ chịu lăn theo mũi tên của nó, chạm một cuộn để cầm lên.", focus: "shelf" },
      { text: "Thả cuộn vào một ô trống là lụa tự trải thẳng tới thành hộp. Thử ô có ngôi sao lấp lánh nè!", focus: "demo" },
    ],
  },
  {
    id: "silk-stop",
    steps: [
      { text: "Lụa đụng lụa đã trải là dừng lại ngay. Trải một hàng trước, cột thả sau sẽ dừng đúng trên hàng đó.", focus: "box" },
      { text: "Phủ kín mọi ô là gói xong quà, khách vui hết cỡ!", focus: "demo" },
    ],
  },
  {
    id: "silk-stars",
    steps: [
      { text: "Con số cạnh hồng tâm là mục tiêu ba sao. Càng ít cuộn càng nhiều sao, cuộn dư cứ để yên trên kệ.", focus: "target" },
      { text: "Hoàn tác thoải mái, không mất gì hết. Sao chỉ tính các cuộn còn nằm trong hộp lúc gói xong.", focus: "undo" },
    ],
  },
  {
    id: "silk-hint",
    steps: [{ text: "Bí quá thì chạm bóng đèn. Mỗi màn có 2 gợi ý miễn phí, mỗi gợi ý chỉ một cuộn kế tiếp thôi nha.", focus: "hint" }],
  },
  {
    id: "silk-bows",
    steps: [
      { text: "Chào mừng tới chương Nơ thắt sẵn! Nơ đã thắt thì không cần phủ, mà lụa đụng nơ cũng dừng lại luôn.", focus: "bows" },
      { text: "Coi nơ như bức tường nhỏ. Ô kẹt giữa hai chiếc nơ hay cần một cuộn riêng đó.", focus: "box" },
    ],
  },
  {
    id: "silk-four",
    steps: [{ text: "Kệ có thêm cuộn lăn sang trái và lăn lên trên. Ô hẹp sát nơ dễ phủ hơn khi lụa chạy về phía nơ đó.", focus: "shelf" }],
  },
  {
    id: "silk-first",
    steps: [
      { text: "Bí kíp của chương này: trải đường viền trước, các cột thả sau sẽ dừng ngay trên viền.", focus: "box" },
      { text: "Hàng giữa trông dài y như viền, nhưng trải nó trước là cắt đôi các cột đó. Tính kỹ xíu nha!", focus: "target" },
    ],
  },
  {
    id: "silk-hanky",
    steps: [{ text: "Khăn tay là cuộn ngắn, số chấm trên cuộn là số ô tối đa nó trải được. Gặp chỗ trống vừa khít là đặt liền.", focus: "shelf" }],
  },
  {
    id: "silk-scissors",
    steps: [
      { text: "Kéo phép nè! Lụa chạm ô có kéo thì phủ luôn ô đó rồi bị cắt ngay tại chỗ.", focus: "scissors" },
      { text: "Đặt cuộn ngay trên ô kéo thì nó chỉ phủ đúng một ô thôi. Dùng kéo để chặn lụa giữa đường nha.", focus: "box" },
    ],
  },
  {
    id: "silk-thread",
    steps: [
      { text: "Ô thêu chỉ nhận lụa chạy dọc theo đường chỉ. Chỉ ngang thì chỉ nhận lụa ngang, chỉ dọc thì chỉ nhận lụa dọc.", focus: "threads" },
      { text: "Lụa chạy ngược đường chỉ sẽ dừng ngay trước ô thêu, coi như gặp tường vậy.", focus: "box" },
    ],
  },
  {
    id: "silk-combo",
    steps: [{ text: "Chương Kéo gặp chỉ thêu! Kéo cắt lụa, ô thêu chặn lụa chạy ngược đường chỉ. Tìm chỗ lụa dừng trước khi thả nha.", focus: "box" }],
  },
  {
    id: "silk-tricks",
    steps: [{ text: "Chương Mẹo thợ may: khăn tay, kéo phép và chỉ thêu chung một hộp. Nhìn kỹ từng ô rồi hẵng thả nha.", focus: "box" }],
  },
  {
    id: "silk-masterpiece",
    steps: [
      { text: "Hộp kiệt tác! Hộp to nhất, đủ mọi bí kíp của xưởng.", focus: "box" },
      { text: "Kệ có thêm cuộn đôi, đặt ở đâu cũng trải về cả hai phía. Dùng nó thay cuộn trái hay cuộn phải đều được nha.", focus: "shelf" },
    ],
  },
];

export const SILK_TUTORIAL_BY_ID: ReadonlyMap<string, SilkTutorial> = new Map(SILK_TUTORIALS.map((tutorial) => [tutorial.id, tutorial]));
